import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ApiService } from '../../../api/services.gen'
import { useAlert } from '../../../constants/AlertProvider'

export function AddProjects({ applicationId }) {
    const [projects, setProjects] = useState([]);
    // States for new project form
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [link, setLink] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const showAlert = useAlert();

    // Fetch existing projects
    useEffect(() => {
        const fetchProjects = async () => {
            if (!applicationId) return
            try {
                const res = await ApiService.apiProjectList({ application: applicationId })
                setProjects(res.results || res)
            } catch (e) {
                showAlert("Failed to load projects", "error");
            }
        }
        fetchProjects();
    }, [applicationId, showAlert])

    // POST: Add new project
    const addProject = async () => {
        if (projects.length >= 5) {
            showAlert("Only 5 projects can be added", "error");
            return;
        }
        const newProject = {
            name,
            description,
            link,
            start_date: startDate ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-01` : null,
            end_date: endDate ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-01` : null,
            application: applicationId,
        }

        if (!newProject.name || !newProject.description || !newProject.start_date) {
            showAlert("Project Name, Description, and Start Date are required.", "error");
            return;
        }

        try {
            const res = await ApiService.apiProjectCreate({ requestBody: newProject })
            setProjects([...projects, res])
            // Reset form
            setName(""); setDescription(""); setLink(""); setStartDate(null); setEndDate(null);
        } catch (error) {
            showAlert("Failed to add project", "error");
            console.log("Failed to add project", error);
        }
    }

    // PATCH: Update project
    const handleFieldChange = (id, field, value) => {
        setProjects(
            projects.map((proj) =>
                proj.id === id ? { ...proj, [field]: value } : proj
            )
        );
    };

    const updateProject = async (id) => {
        const updatedProject = projects.find((proj) => proj.id === id);
        if (!updatedProject) return;

        try {
            await ApiService.apiProjectPartialUpdate({
                id,
                requestBody: updatedProject,
            });
            showAlert("Project updated successfully!", "success");
        } catch (e) {
            showAlert("Failed to update project", "error");
        }
    };

    const removeProject = async (id) => {
        try {
            await ApiService.apiProjectDestroy({ id });
            setProjects(projects.filter((proj) => proj.id !== id));
            showAlert("Project removed successfully!", "success");
        } catch (e) {
            showAlert("Failed to delete project", "error");
        }
    };

    return (
        <div className="flex flex-col gap-y-4">
            {projects.map((proj) => (
                <div key={proj.id} className="p-2 flex flex-col gap-2 mb-2 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                        <span className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">{proj.name}</span>
                        <button className="text-gray-400 dark:text-gray-500 flex flex-row gap-x-2 items-center justify-center hover:text-gray-500 dark:hover:text-gray-300 cursor-pointer" onClick={() => removeProject(proj.id)}>
                            <XMarkIcon className="size-4" />
                            <span className="text-[14px] font-medium">Remove</span>
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Project Name</label>
                            <input type="text" value={proj.name || ""} onChange={e => handleFieldChange(proj.id, "name", e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Project URL</label>
                            <input type="url" value={proj.link || ""} onChange={e => handleFieldChange(proj.id, "link", e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Start Date</label>
                            <DatePicker selected={proj.start_date ? new Date(proj.start_date) : null} onChange={date => handleFieldChange(proj.id, "start_date", date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01` : null)} dateFormat="yyyy/MM" showMonthYearPicker className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">End Date</label>
                            <DatePicker selected={proj.end_date ? new Date(proj.end_date) : null} onChange={date => handleFieldChange(proj.id, "end_date", date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01` : null)} dateFormat="yyyy/MM" showMonthYearPicker className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                        </div>
                    </div>
                    <div className="px-2 flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Description</label>
                        <textarea rows="4" maxLength={1500} value={proj.description || ""} onChange={e => handleFieldChange(proj.id, "description", e.target.value)} className="p-2 resize-none border border-gray-300 text-[14px] focus:outline-none focus:border-blue-500 rounded-lg" />
                    </div>
                    <div className='flex justify-end'>
                        <button type="button" className="text-[14px] font-semibold text-blue-700 dark:text-blue-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-300 flex items-center gap-1" onClick={() => updateProject(proj.id)}>Update</button>
                    </div>
                </div>
            ))}
            {projects.length < 5 ? (
                <>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-y-2"><label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Project Name</label><input type="text" value={name} onChange={e => setName(e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" /></div>
                        <div className="flex flex-col gap-y-2"><label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Project URL</label><input type="url" value={link} onChange={e => setLink(e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" placeholder="https://my-project.com"/></div>
                        <div className="flex flex-col gap-y-2"><label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Start Date</label><DatePicker selected={startDate} onChange={date => setStartDate(date)} dateFormat="yyyy/MM" showMonthYearPicker className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" /></div>
                        <div className="flex flex-col gap-y-2"><label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">End Date</label><DatePicker selected={endDate} onChange={date => setEndDate(date)} dateFormat="yyyy/MM" showMonthYearPicker className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" /></div>
                    </div>
                    <div className="px-2 flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Description</label>
                        <textarea rows="4" maxLength={1500} value={description} onChange={e => setDescription(e.target.value)} className="p-2 resize-none border border-gray-300 text-[14px] focus:outline-none focus:border-blue-500 rounded-lg" />
                    </div>
                </>
            ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">You can only add up to 5 project entries.</div>
            )}
            <div className='flex justify-end'>
                <button type="button" onClick={addProject} className={`text-[14px] font-semibold ${projects.length >= 5 ? "cursor-none text-blue-300 dark:text-blue-800" : "text-blue-700 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer"} flex items-center gap-1`} disabled={projects.length >= 5}>
                    Add Project
                </button>
            </div>
        </div>
    );
}